import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WishlistContext = createContext({ ids: new Set(), toggle: () => {}, loading: false });

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/wishlist");
      setIds(new Set(data.map((p) => p.id)));
    } catch {
      setIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = async (productId) => {
    if (!user) return { needAuth: true };
    if (ids.has(productId)) {
      await api.delete(`/wishlist/${productId}`);
      setIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      return { added: false };
    } else {
      await api.post("/wishlist", { product_id: productId });
      setIds((prev) => new Set(prev).add(productId));
      return { added: true };
    }
  };

  return (
    <WishlistContext.Provider value={{ ids, toggle, loading, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
}
